/**
 * COC follow-up report view-model (COC-REPORTING-GRAINS-V1, activity 21).
 * PURE: no fetch, no React -- unit-pinned without jsdom (the repo's
 * *-view.ts idiom).
 *
 * The backend (lib/domain/patient/followup-report.ts, via
 * GET /api/coc/patient-followups/report) already groups, sums and sorts
 * (worst clinic first by overdueCases, highest-volume treatment first by
 * openItems) -- this module only formats the two count lines the page's
 * DataTable `countLine` slot needs, and re-exports the shared money
 * formatter (inrLabel, manager-tracking/manager-cockpit-view.ts) rather
 * than re-deriving one.
 */

import { inrLabel } from '@/dashboard/manager-tracking/manager-cockpit-view';
import type {
  CocFollowupClinicReportRowView,
  CocFollowupReportTotalsView,
  CocFollowupTreatmentReportRowView,
} from '@/dashboard/api';

export { inrLabel };

/** "5 clinics - 42 open cases - 7 overdue" (the overdue clause omitted
 *  when zero, an honest "nothing overdue" rather than a noisy "0"). */
export function clinicCountLine(
  rows: readonly CocFollowupClinicReportRowView[],
  totals: CocFollowupReportTotalsView,
): string {
  const clinicWord = rows.length === 1 ? 'clinic' : 'clinics';
  const caseWord = totals.openCases === 1 ? 'case' : 'cases';
  const base = `${rows.length} ${clinicWord} - ${totals.openCases} open ${caseWord}`;
  return totals.overdueCases > 0 ? `${base} - ${totals.overdueCases} overdue` : base;
}

/** "12 treatments - 58 open items - 4 advised" (the advised clause
 *  omitted when zero). */
export function treatmentCountLine(
  rows: readonly CocFollowupTreatmentReportRowView[],
  totals: CocFollowupReportTotalsView,
): string {
  const treatmentWord = rows.length === 1 ? 'treatment' : 'treatments';
  const itemWord = totals.openItems === 1 ? 'item' : 'items';
  const base = `${rows.length} ${treatmentWord} - ${totals.openItems} open ${itemWord}`;
  return totals.advisedItems > 0 ? `${base} - ${totals.advisedItems} advised` : base;
}
